import { ApiResponse, GlobalStore } from "@roc/feature-app-core";
import { makeObservable, flow, observable, action, computed } from "mobx";
import { DocReviewBaseService } from "../service/docReviewBaseService";
import { AIDocType } from "@roc/feature-documents";
import { ReviewLogStore } from "./reviewLogStore";

export abstract class DocReviewBaseStore {
  public globalStore: GlobalStore;
  public reviewLogStore: ReviewLogStore;
  protected service: DocReviewBaseService;
  public docReviewData: any = [];
  public docChecklist: any = [];
  public updatedSections: Map<string, any> = new Map();
  public showAllOnRevision: boolean = false;
  public automationStatus: string;

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.reviewLogStore = new ReviewLogStore(globalStore);
    makeObservable(this, {
      getDocReviewData: flow,
      saveDocReviewData: flow,
      docReviewData: observable,
      automationStatus: observable,
      readOnlyForm: computed,
      handleItemChange: action,
      reviewDataAvailable: computed,
      showAllOnRevision: observable,
      toggleShowAllOnRevision: action,
      getFlaggedItemCount: action,
      getSectionItems: action,
      setRevisionComment: flow,
      sendRevisionDoc: flow,
      clearStore: action,
      getDocChecklist: flow,
      docChecklist: observable,
      onChangeDocListItem: action,
      onChangeFullChecklist: action,
      fullChecklistStatus: computed
    });
  }


  *getDocReviewData(loanId: number, type: AIDocType) {
    try {
      this.clearStore();
      const response: ApiResponse = yield this.service.getDocReviewData(loanId, this.getTypeKey(type));
      this.docReviewData = JSON.parse(response.data.data);
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error getting review data',
      });
    }
  }


  *getDocChecklist(loanId: number, type: AIDocType) {
    try {
      const response: ApiResponse = yield this.service.getDocChecklist(loanId, this.getTypeKey(type));
      this.docChecklist = response.data.data;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error getting document checklist',
      });
    }
  }

  *saveDocReviewData(loanId: number, type: AIDocType) {
    try {
      yield this.reviewLogStore.logEvents(loanId, type);
      yield this.service.saveDocReviewData(loanId, this.getTypeKey(type), [...this.updatedSections.values()]);
      yield this.service.saveDocChecklist(this.docChecklist);
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error saving review data',
      });
      throw e;
    }
  }

  *sendRevisionDoc(loanId: number, type: AIDocType) {
    try {
      yield this.saveDocReviewData(loanId, type);
      yield this.service.sendRevisionDoc(loanId, this.getTypeKey(type));
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error sending revision document',
      });
      throw e;
    }
  }

  private getTypeKey(type: AIDocType) {
    return Object.keys(AIDocType).find(key => AIDocType[key] === type)
  }

  get reviewDataAvailable() {
    return this.docReviewData.some(section => section && (
      section.status === 'IN_PROGRESS' ||
      section.status === 'FAILED' ||
      (section.items || []).some(item => this.showAllOnRevision || item.sendForRevision)
    ));
  }

  handleItemChange(column: string, sectionName: string, key: string, value: any) {
    this.docReviewData = this.docReviewData.map(section => section.sectionName == sectionName ? {
      ...section,
      items: section.items.map(item =>
        item.itemDisplayName === key
          ? {
            ...item,
            [column]: value,
          }
          : item
      ),
    } : section);
    this.updatedSections.set(sectionName, this.getSectionByFileName(sectionName));
  }


  getFlaggedItemCount(sectionName: string) {
    const allItems = this.getSectionItems(sectionName);
    return allItems.filter(item => item.sendForRevision).length;
  }

  getSectionItems(sectionName: string) {
    return this.docReviewData.find(section => section.sectionName === sectionName)?.items
  }

  get sectionsMarkedForRevision() {
    return this.docReviewData.filter(section => section && (section.items || []).some(item => item.sendForRevision))
  }

  toggleShowAllOnRevision() {
    this.showAllOnRevision = !this.showAllOnRevision;
  }

  get readOnlyForm() {
    return this.automationStatus === "IN_PROGRESS";
  }

  * setRevisionComment(docType: AIDocType, item: any) {
    try {
      if (!item.reviewerComment) {
        const response = yield this.service.getRevisionComment(this.getTypeKey(docType), `${this.getSectionByFileName(item.sectionName)?.type}-${item.itemName}`);
        const { comment } = response.data.data;
        this.handleItemChange('reviewerComment', item.sectionName, item.itemDisplayName, comment);
      }
    } catch (e) {
      console.log('Error getting revision comment', e)
    }
  }

  getSectionByFileName(fileName: string) {
    return this.docReviewData.find(section => section.sectionName === fileName);
  }

  clearStore() {
    this.docReviewData = [];
    this.reviewLogStore.reset();
    this.updatedSections = new Map();
  }

  onChangeDocListItem(itemName: string, field: string, value: any) {
    this.docChecklist = this.docChecklist.map(doc => doc.name == itemName ? {
      ...doc,
      [field]: value
    } : doc);
  }

  onChangeFullChecklist(value: boolean) {
    this.docChecklist = this.docChecklist.map(item => {
      return {
        ...item,
        checked: value
      }
    });
  }

  get fullChecklistStatus() {
    return this.docChecklist.every(item => item.checked);
  }

}
